import { FC } from "react";

import Box from "@mui/material/Box";
import Portal from "@mui/material/Portal";
import dynamic from "next/dynamic";

import { mergeSx } from "@/common/helpers/mergeSx";
import { unstyledButtonSx } from "@/common/helpers/unstyledButtonSx";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import FullScreenImageDisplay from "@/components/MultipleImageDisplay/FullScreenImageDisplay";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useFullScreenImage } from "@/components/MultipleImageDisplay/useFullScreenImage";
import { FormZeffyLoader } from "@/forms-design-system/FormZeffyLoader";

// eslint-disable-next-line react/display-name
const Image = dynamic(() => import("@/components/Image/Image"), { loading: () => <FormZeffyLoader /> });

interface DrawerImageDisplayProps {
  photoUrls: string[];
  title: string;
}

export const DrawerImageDisplay: FC<DrawerImageDisplayProps> = ({ photoUrls, title }) => {
  const { isSmallScreen } = useMediaQuery();
  const { displayedImageIndex, isFullScreen, goLeft, goRight, closeFullScreen, openFullScreen, setSelectedIndex } =
    useFullScreenImage();

  if (photoUrls.length === 0) {
    return null;
  }

  const handleImageClick = (index: number) => {
    setSelectedIndex(index);
    openFullScreen();
  };

  return (
    <Box>
      <Portal>
        <FullScreenImageDisplay
          photoUrls={photoUrls}
          title={title}
          displayedImageIndex={displayedImageIndex}
          isFullScreen={isFullScreen}
          goLeft={goLeft}
          goRight={goRight}
          closeFullScreen={closeFullScreen}
          setSelectedIndex={setSelectedIndex}
        />
      </Portal>
      {isSmallScreen ? (
        <MobileScroll photoUrls={photoUrls} title={title} onImageClick={handleImageClick} />
      ) : (
        <DesktopGrid photoUrls={photoUrls} title={title} onImageClick={handleImageClick} />
      )}
    </Box>
  );
};

interface ImageListProps {
  photoUrls: string[];
  title: string;
  onImageClick: (index: number) => void;
}

const imageButtonSx = mergeSx(unstyledButtonSx, (theme) => ({
  display: "block",
  cursor: "zoom-in",
  borderRadius: "8px",
  "&:focus-visible": {
    outline: `3px solid ${theme.palette.formsComponents?.global.focusOutlineColor}`,
    outlineOffset: "-3px",
  },
}));

const MOBILE_IMAGE_SIZE = 120;

const MobileScroll: FC<ImageListProps> = ({ photoUrls, title, onImageClick }) => {
  const { t } = useTranslate();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        flexWrap: "nowrap",
        WebkitOverflowScrolling: "touch",
        scrollSnapType: "x mandatory",
        margin: "0 auto",
      }}
    >
      {photoUrls.map((photoUrl, index) => (
        <Box
          key={index}
          component="button"
          type="button"
          aria-label={t("common", "a11y.enlargeImage", { index: index + 1, total: photoUrls.length })}
          sx={mergeSx(imageButtonSx, {
            width: MOBILE_IMAGE_SIZE,
            height: MOBILE_IMAGE_SIZE,
            flex: "0 0 auto",
            scrollSnapAlign: "start",
            marginRight: index === photoUrls.length - 1 ? 3.5 : 1,
          })}
          onClick={() => onImageClick(index)}
        >
          <Image
            nextImage
            src={photoUrl}
            alt={title ? t("common", "a11y.itemPhoto", { item: title }) : ""}
            width={MOBILE_IMAGE_SIZE}
            height={MOBILE_IMAGE_SIZE}
            objectFit="cover"
            data-test="rate-image"
            resolution={MOBILE_IMAGE_SIZE * 2}
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      ))}
    </Box>
  );
};

const DESKTOP_IMAGE_SIZE = 174;

const DesktopGrid: FC<ImageListProps> = ({ photoUrls, title, onImageClick }) => {
  const { t } = useTranslate();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 1.5,
        overflowX: "auto",
        overflowY: "hidden",
        flexWrap: "nowrap",
        WebkitOverflowScrolling: "touch",
        scrollSnapType: "x mandatory",
      }}
    >
      {photoUrls.map((photoUrl, index) => (
        <Box
          key={index}
          component="button"
          type="button"
          aria-label={t("common", "a11y.enlargeImage", { index: index + 1, total: photoUrls.length })}
          sx={mergeSx(imageButtonSx, {
            width: DESKTOP_IMAGE_SIZE,
            height: DESKTOP_IMAGE_SIZE,
            flex: "0 0 auto",
            scrollSnapAlign: "start",
            overflow: "hidden",
          })}
          onClick={() => onImageClick(index)}
        >
          <Image
            nextImage
            src={photoUrl}
            alt={title ? t("common", "a11y.itemPhoto", { item: title }) : ""}
            width={DESKTOP_IMAGE_SIZE}
            height={DESKTOP_IMAGE_SIZE}
            objectFit="cover"
            data-test="rate-image"
            resolution={DESKTOP_IMAGE_SIZE * 2}
            sx={{ width: "100%", height: "100%", display: "block" }}
          />
        </Box>
      ))}
    </Box>
  );
};
